import { Alert, Box, Divider, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { config } from '../../core/config/env';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

interface GoogleCredentialResponse {
  credential?: string;
}

interface GoogleAccountsId {
  initialize(options: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
  }): void;
  renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } };
  }
}

let scriptPromise: Promise<void> | null = null;

/** Load the Google Identity Services script once per page load. */
function loadGisScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google sign-in')));
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google sign-in'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface GoogleSignInButtonProps {
  /** Receives the Google ID token (JWT) to exchange with our backend. */
  onCredential: (idToken: string) => void | Promise<void>;
  /** Button caption — "signin_with" on login, "signup_with" on registration. */
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  disabled?: boolean;
}

/**
 * Renders Google's official sign-in button. Nothing is rendered when no
 * client id is configured, so the app still runs without Google set up.
 */
export function GoogleSignInButton({
  onCredential,
  text = 'signin_with',
  disabled = false,
}: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Keep the latest callback without re-running the one-shot GIS setup.
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  const clientId = config.googleClientId;

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    loadGisScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (!response.credential) {
              setError('Google did not return a credential. Please try again.');
              return;
            }
            void onCredentialRef.current(response.credential);
          },
        });
        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text,
          shape: 'rectangular',
          logo_alignment: 'left',
          width: containerRef.current.clientWidth || 320,
        });
      })
      .catch(() => {
        if (!cancelled) setError('Could not load Google sign-in.');
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, text]);

  if (!clientId) return null;

  return (
    <Box>
      <Divider sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          or
        </Typography>
      </Divider>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          minHeight: 44,
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      />
    </Box>
  );
}
